// Wave 20.47g — rider pose illustration per position class.
//
// Larger viewbox (200×140) with proper limbs, layered shading, and
// gradient fills so the glyph looks intentional at 200 px card
// size, not a shrunken schematic. Each pose captures the essence
// of the position (torso angle, arm angle, back curvature).
//
// Bike is stroke-only in a neutral gray; the RIDER is filled with
// the class palette color so the aero-pane row's chip color and
// the pose art always agree.

import React from 'react';
import { positionColor } from '../services/positionColors';

interface Props {
  positionClass: string;
  size?: number;
}

const BIKE = '#B0B7C4';
const OUTLINE = '#0a100c';
const SKIN = '#E8B48F';

let seq = 0;
function useGlyphId(prefix: string): string {
  const [n] = React.useState(() => ++seq);
  return `${prefix}-${n}`;
}

/**
 * Shared bike frame + wheels. Coordinate system is a 200×140
 * viewBox with:
 *   left wheel center  = (46, 116)
 *   right wheel center = (154, 116)
 *   bottom bracket     = (100, 100)
 *   head tube top      = (128, 68)
 *   seat post top      = (72, 68)
 *   handlebar stem     = (128, 68) → forks
 */
function Bike() {
  return (
    <g fill="none" stroke={BIKE} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
      <circle cx={46} cy={116} r={18} />
      <circle cx={154} cy={116} r={18} />
      <circle cx={46} cy={116} r={11} strokeWidth={1.5} opacity={0.5} />
      <circle cx={154} cy={116} r={11} strokeWidth={1.5} opacity={0.5} />
      {/* main triangle: BB - seat - HT - BB */}
      <path d="M100 100 L72 68 L128 68 L100 100 Z" />
      {/* seat + head tubes to wheels */}
      <path d="M100 100 L46 116" />
      <path d="M128 68 L154 116" />
      {/* fork */}
      <path d="M128 68 L146 78 L154 116" />
      {/* saddle */}
      <path d="M62 65 L82 65" strokeWidth={4} />
      {/* seatpost */}
      <path d="M72 68 L72 62" strokeWidth={2.4} />
    </g>
  );
}

interface RiderPose {
  // Torso: from hip (72, 68) to shoulder
  shoulder: [number, number];
  // Head circle
  head: [number, number];
  headR?: number;
  // Arms: shoulder → elbow → hand (on bars)
  elbow: [number, number];
  hand: [number, number];
  // Front leg: hip → knee → foot at bottom bracket
  knee?: [number, number];
  foot?: [number, number];
}

/** All poses share hip = seat top = (72, 65). */
const HIP: [number, number] = [72, 65];

function Rider({ pose, color, id }: { pose: RiderPose; color: string; id: string }) {
  const [sx, sy] = pose.shoulder;
  const [ex, ey] = pose.elbow;
  const [hx, hy] = pose.hand;
  const [headX, headY] = pose.head;
  const headR = pose.headR ?? 10;
  const knee = pose.knee ?? [95, 90];
  const foot = pose.foot ?? [100, 100];
  return (
    <g>
      <defs>
        <linearGradient id={`${id}-jersey`} x1={HIP[0]} y1={HIP[1]} x2={sx} y2={sy} gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={color} stopOpacity={0.75} />
          <stop offset="1" stopColor={color} />
        </linearGradient>
      </defs>
      {/* leg — behind body */}
      <g stroke={OUTLINE} strokeWidth={7} strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d={`M ${HIP[0]} ${HIP[1] + 4} L ${knee[0]} ${knee[1]} L ${foot[0]} ${foot[1]}`} />
      </g>
      <g stroke={color} strokeWidth={4.5} strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d={`M ${HIP[0]} ${HIP[1] + 4} L ${knee[0]} ${knee[1]}`} />
        <path d={`M ${knee[0]} ${knee[1]} L ${foot[0]} ${foot[1]}`} />
      </g>
      {/* torso — thick colored bar with outline */}
      <path
        d={`M ${HIP[0]} ${HIP[1]} L ${sx} ${sy}`}
        stroke={OUTLINE}
        strokeWidth={14}
        strokeLinecap="round"
        fill="none"
      />
      <path
        d={`M ${HIP[0]} ${HIP[1]} L ${sx} ${sy}`}
        stroke={`url(#${id}-jersey)`}
        strokeWidth={10}
        strokeLinecap="round"
        fill="none"
      />
      {/* arm */}
      <g stroke={OUTLINE} strokeWidth={7} strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d={`M ${sx} ${sy} L ${ex} ${ey} L ${hx} ${hy}`} />
      </g>
      <g stroke={color} strokeWidth={4} strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d={`M ${sx} ${sy} L ${ex} ${ey}`} />
      </g>
      <g stroke={SKIN} strokeWidth={4} strokeLinecap="round" fill="none">
        <path d={`M ${ex} ${ey} L ${hx} ${hy}`} />
      </g>
      {/* helmet — teardrop */}
      <path
        d={`M ${headX - headR * 0.9} ${headY} A ${headR} ${headR} 0 1 1 ${headX + headR * 0.9} ${headY} L ${headX + headR * 1.6} ${headY + headR * 0.4} L ${headX - headR * 0.9} ${headY} Z`}
        fill={color}
        stroke={OUTLINE}
        strokeWidth={2}
        strokeLinejoin="round"
      />
      {/* face patch */}
      <circle cx={headX + headR * 0.6} cy={headY + headR * 0.4} r={headR * 0.55} fill={SKIN} />
    </g>
  );
}

const POSES: Record<string, RiderPose> = {
  // Wave 20.49cj — Off-saddle standing. Torso vertical, hips
  // ahead of the saddle, elbows out. Highest CdA silhouette.
  standing: {
    shoulder: [64, 20],
    head: [70, 4],
    elbow: [96, 30],
    hand: [126, 60],
    knee: [88, 82],
    foot: [100, 100],
  },
  // Sitting tall. Torso near-vertical, arms slightly bent to hoods.
  tops: {
    shoulder: [76, 30],
    head: [82, 16],
    elbow: [105, 42],
    hand: [128, 66],
    knee: [95, 90],
    foot: [100, 100],
  },
  // Slight forward lean, hands on hoods.
  hoods: {
    shoulder: [86, 34],
    head: [96, 22],
    elbow: [112, 46],
    hand: [128, 66],
    knee: [95, 90],
    foot: [100, 100],
  },
  // Deep flat back, hands in the drops (lower + forward).
  drops: {
    shoulder: [100, 44],
    head: [116, 42],
    elbow: [124, 60],
    hand: [136, 76],
    knee: [95, 90],
    foot: [100, 100],
  },
  // Aero tuck — butt back off saddle, shoulders very low.
  aero_tuck: {
    shoulder: [110, 48],
    head: [128, 46],
    headR: 9,
    elbow: [128, 60],
    hand: [138, 74],
    knee: [95, 90],
    foot: [100, 100],
  },
  // TT bars — forearms level, super long low tuck.
  tt: {
    shoulder: [104, 40],
    head: [140, 38],
    headR: 9,
    elbow: [128, 44],
    hand: [156, 44],
    knee: [95, 90],
    foot: [100, 100],
  },
};

export default function PoseGlyph({ positionClass, size = 200 }: Props) {
  const id = useGlyphId('pg');
  const color = positionColor(positionClass);
  const pose = POSES[positionClass] ?? POSES.tops;
  const w = size;
  const h = Math.round(size * 0.7);
  return (
    <svg
      width={w}
      height={h}
      viewBox="0 0 200 140"
      role="img"
      aria-label={`${positionClass} pose`}
    >
      <Bike />
      <Rider pose={pose} color={color} id={id} />
    </svg>
  );
}
